import React from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { AppBar, Toolbar, Typography, Button, Box } from "@mui/material";
import { routes } from "./router/routes";
import "./App.css";
import { EventDataProvider, useEventData } from "./contexts/EventDataContext";
import { useParams, useLocation } from "react-router-dom";
import { db } from "./firebase/config";
import { doc, getDoc } from "firebase/firestore";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { loadGoogleFont, getGoogleFontName, isFontAvailable } from "./utils/fontLoader";
import AccessDenied from "./components/AccessDenied";
import Loading from "./components/Loading";

function MainLayout({ Component }) {
  const { eventData, isLoading } = useEventData();
  const [fontLoaded, setFontLoaded] = React.useState(false);
  const [showLoading, setShowLoading] = React.useState(true);

  React.useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(true);
    }
  }, [isLoading]);

  const fontFamily = eventData?.theme?.selectedFont?.value;
  // console.log("Selected font:", fontFamily);

  // Load the Google Font when fontFamily changes
  React.useEffect(() => {
    if (fontFamily && fontFamily.trim() !== '') {
      const googleFontName = getGoogleFontName(fontFamily);
      // console.log("Loading Google Font:", googleFontName);

      loadGoogleFont(googleFontName)
        .then(() => {
          setFontLoaded(true);
          console.log("Font loaded successfully:", googleFontName);
        })
        .catch((error) => {
          console.warn("Font loading failed, using fallback:", error);
          setFontLoaded(true); // Still proceed with fallback fonts
        });
    } else {
      setFontLoaded(true);
    }
  }, [fontFamily]);

  const theme = React.useMemo(() => {
    // Get the actual Google Font name
    const googleFontName = fontFamily ? getGoogleFontName(fontFamily) : null;

    // Ensure we have a valid font family, with proper fallbacks
    const safeFontFamily = googleFontName && googleFontName.trim() !== ''
      ? [
        `"${googleFontName}"`, // Wrap custom font in quotes
        '"Noto Sans Thai"', // Primary fallback
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'Arial', // Additional fallback
        'sans-serif' // Final fallback
      ].join(',')
      : '"Noto Sans Thai", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

    // console.log("Theme font family:", safeFontFamily);

    return createTheme({
      typography: {
        fontFamily: safeFontFamily,
        allVariants: {
          fontFamily: safeFontFamily,
        },
      },
      components: {
        MuiSelect: {
          styleOverrides: {
            root: {
              fontFamily: safeFontFamily,
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontFamily: safeFontFamily,
            },
          },
        },
        // Add more components that should inherit the font
        MuiTypography: {
          styleOverrides: {
            root: {
              fontFamily: safeFontFamily,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              fontFamily: safeFontFamily,
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              fontFamily: safeFontFamily,
            },
            '& .MuiInputBase-input': {
              fontFamily: safeFontFamily,
            },
            '& .MuiFormLabel-root': {
              fontFamily: safeFontFamily,
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              fontFamily: safeFontFamily,
            },
          },
        },
        // Override global CSS baseline
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              fontFamily: safeFontFamily,
            },
            '*': {
              fontFamily: `${safeFontFamily} !important`,
            },
          },
        },
      },
    });
  }, [fontFamily, fontLoaded]);

  if (showLoading) {
    const bgImage = eventData?.main?.backgroundImage?.value || eventData?.theme?.backgroundImage?.value;
    const mainColor = eventData?.theme?.mainColor?.value;
    const backgroundColor = eventData?.theme?.backgroundColor?.value;
    return <Loading backgroundImage={bgImage} mainColor={mainColor} backgroundColor={backgroundColor} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Component />
    </ThemeProvider>
  );
}

// Component to wrap route elements and check for ID parameter
function RouteWrapper({ Component }) {
  const { id } = useParams();
  const [checkedId, setCheckedId] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const location = useLocation();

  const [backgroundImage, setBackgroundImage] = React.useState(null);
  const [mainColor, setMainColor] = React.useState(null);
  const [backgroundColor, setBackgroundColor] = React.useState(null);

  // Check if current route requires an ID parameter

  // Log or validate the ID parameter
  React.useEffect(() => {
    if (id) {
      console.log("ID parameter found:", id);
      // Add your ID validation logic here
      // For example, check if ID exists in your database
      const docRef = doc(db, "events", id);
      const checkDoc = getDoc(docRef);
      checkDoc
        .then((docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            const eventStatus = data?.information?.eventStatus;
            const isPreview = location.pathname.includes("/preview");

            // Extract background image and colors
            const bgImage = data?.main?.backgroundImage?.value || data?.theme?.backgroundImage?.value;
            const mColor = data?.theme?.mainColor?.value;
            const bgColor = data?.theme?.backgroundColor?.value;

            setBackgroundImage(bgImage);
            setMainColor(mColor);
            setBackgroundColor(bgColor);

            console.log("Event Status:", eventStatus);
            console.log("Is Preview:", isPreview);

            if (eventStatus === "publish" || isPreview) {
              console.log("Event ID is valid and accessible:", id);
              setCheckedId(true);
            } else {
              console.warn("Event is not published and not in preview mode.");
              setCheckedId(false);
            }
            setLoading(false);
          } else {
            console.error("No such document with ID:", id);
            setCheckedId(false);
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error("Error checking document:", error);
          setCheckedId(false);
        });
    } else {
      console.log("No ID required for this route");
      setCheckedId(true);
      setLoading(false);
    }
  }, [id, location.pathname]);

  if (loading) {
    return <Loading backgroundImage={backgroundImage} mainColor={mainColor} backgroundColor={backgroundColor} />;
  }

  if (!id) {
    // If no ID is provided and the route does not require it, render the component directly
    return <AccessDenied />;
  }

  if (!checkedId) {
    return <AccessDenied />;
  }

  return (
    <EventDataProvider id={id}>
      <MainLayout Component={Component} />
      {/* Render the component with the provided ID */}
    </EventDataProvider>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<RouteWrapper Component={route.element} />}
            />
          ))}
        </Routes>
      </div>
    </Router>
  );
}

export default App;
